<template>
  <div class="adduser" style="min-height:85vh">
    <div style="margin: 16px 0; display:flex; align-items:center; flex-wrap:wrap; gap:12px">
      <a-breadcrumb>
        <a-breadcrumb-item style="font-size:30px;">{{ pageTitle }}</a-breadcrumb-item>
      </a-breadcrumb>
      <a-button @click="$router.back()">Back</a-button>
    </div>

    <a-spin :spinning="initialLoading">
      <a-card>
        <a-row :gutter="24">
          <a-col :xs="24" :md="12">
            <a-form-item label="Country" :required="true">
              <a-select
                show-search
                :value="form.countryId"
                :filter-option="filterCountryOption"
                placeholder="Select country"
                @change="handleCountryChange"
                :disabled="saving"
                style="width:100%"
              >
                <a-select-option
                  v-for="country in countries"
                  :key="country._id"
                  :value="country._id"
                >
                  {{ country.country }}
                </a-select-option>
              </a-select>
              <small v-if="form.countrySlug" class="slug-hint">
                Slug: /country/{{ form.countrySlug }}
              </small>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item label="Status" :required="true">
              <a-radio-group v-model="form.status" :disabled="saving">
                <a-radio :value="1">Published</a-radio>
                <a-radio :value="0">Draft</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <a-col :xs="24">
            <a-form-item label="Question" :required="true">
              <a-input
                v-model="form.question"
                placeholder="Enter FAQ question"
                :disabled="saving"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item label="Answer" :required="true">
          <vue2-tinymce-editor
            v-model="form.answer"
            :options="tinymceOptions"
            style="min-height:300px;"
          />
        </a-form-item>

        <div style="display:flex; justify-content:flex-end; gap:12px; margin-top:20px;">
          <a-button @click="$router.push('/admin/country-faq')" :disabled="saving">Cancel</a-button>
          <a-button type="primary" :loading="saving" @click="handleSubmit">
            {{ isEdit ? 'Update FAQ' : 'Create FAQ' }}
          </a-button>
        </div>
      </a-card>
    </a-spin>
  </div>
</template>

<script>
import { Vue2TinymceEditor } from "vue2-tinymce-editor";
import { api } from "../../helpers/Helpers";

export default {
  components: {
    Vue2TinymceEditor,
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    faqId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      initialLoading: true,
      saving: false,
      countries: [],
      form: {
        countryId: undefined,
        countrySlug: "",
        question: "",
        answer: "",
        status: 1,
      },
      tinymceOptions: {
        menubar: false,
        plugins: "advlist autolink link lists",
        toolbar:
          "bold italic underline | alignleft aligncenter alignright | bullist numlist | link removeformat",
      },
    };
  },
  computed: {
    isEdit() {
      return this.mode === "edit";
    },
    pageTitle() {
      return this.isEdit ? "Edit Country FAQ" : "Add Country FAQ";
    },
    countryMap() {
      return this.countries.reduce((acc, cur) => {
        acc[cur._id] = cur;
        return acc;
      }, {});
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      try {
        await this.fetchCountries();
        if (this.isEdit) {
          await this.fetchFaqDetails();
        }
      } catch (error) {
        this.$message.error("Failed to load FAQ data.");
        console.error(error);
      } finally {
        this.initialLoading = false;
      }
    },
    async fetchCountries() {
      const res = await api.getCountryInfo();
      if (Array.isArray(res)) {
        this.countries = res.sort((a, b) => (a.country || "").localeCompare(b.country || ""));
      }
    },
    async fetchFaqDetails() {
      if (!this.faqId) {
        this.$message.error("FAQ ID missing.");
        this.$router.replace("/admin/country-faq");
        return;
      }
      const res = await api.getCountryFaq(this.faqId);
      if (!res || !res._id) {
        this.$message.error("FAQ not found.");
        this.$router.replace("/admin/country-faq");
        return;
      }
      this.form = {
        countryId: res.country_id,
        countrySlug: res.country_slug,
        question: res.question,
        answer: res.answer || "",
        status: res.status !== undefined ? res.status : 1,
      };
    },
    filterCountryOption(input, option) {
      const text = option.componentOptions.children[0].text || "";
      return text.toLowerCase().includes(input.toLowerCase());
    },
    handleCountryChange(value) {
      this.form.countryId = value;
      const countryInfo = this.countryMap[value] || {};
      const candidate = countryInfo.slug || countryInfo.country || "";
      this.form.countrySlug = this.slugify(candidate);
    },
    slugify(value) {
      return (value || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    validate() {
      if (!this.form.countryId) {
        this.$message.warning("Please select a country.");
        return false;
      }
      if (!this.form.countrySlug) {
        this.$message.warning("Unable to derive country slug. Please re-select a country.");
        return false;
      }
      if (!this.form.question || !this.form.question.trim()) {
        this.$message.warning("Question is required.");
        return false;
      }
      if (!this.form.answer || !this.form.answer.trim()) {
        this.$message.warning("Answer is required.");
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        country_id: this.form.countryId,
        country_slug: this.form.countrySlug,
        question: this.form.question.trim(),
        answer: this.form.answer,
        status: Number(this.form.status),
      };
    },
    async handleSubmit() {
      if (this.saving) return;
      if (!this.validate()) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await api.updateCountryFaq(this.faqId, payload);
          this.$message.success("FAQ updated successfully.");
        } else {
          await api.createCountryFaq(payload);
          this.$message.success("FAQ created successfully.");
        }
        this.$router.push("/admin/country-faq");
      } catch (error) {
        console.error(error);
        this.$message.error("Failed to save FAQ.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.adduser :deep(.ant-form-item-label) label {
  font-weight: 600;
}
</style>
